/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2020. All rights reserved
------------------------------------------------------------------------------------------------- */
import { dispatchAttributeChangedEvent } from './attribute-changed-event-dispatcher.js';

/** ### ControlStateMixin
 * ___
 * Superclass aimed to control the common states of `tabindex`
 * and `disabled` for the components that import this mixin,
 * thereby reducing code duplication.
 * ___
 * #### How to use this mixin ?
 *
 * Import like this in the component:
 * ```js
 * import { ControlStateMixin } from '@shui-web-components/utils/control-state-mixin.js';
 * ```
 * Then inherit this mixin like this:
 * ```js
 * export class SHComponent extends ControlStateMixin(LitElement)
 * ```
 * ___
 *
 * #### Motivations for using this mixin ?
 *
 * - `tabindex` is supposed to be an author-set attribute that
 *    must not be overriden except for the case of disabled.
 *
 * -  the behavior of `disabled` and the subsequent handling of
 *   `tabindex` is common among components. So grouping such
 *    behaviors in a common class promotes code reusability and
 *    avoids code duplication. This in-turn helps in reducing the
 *    file size of the final build.
 * ___
 *
 * #### How does this mixin work ?
 *
 * - The `tabindex` attribute is mapped to a property which is also named as `tabindex`.
 * The value of `tabindex` property is observed and stored in a private component class-
 * variable named `previousTabIndex`.
 *
 * - When the `disabled` property is set, the `tabindex` attribute is removed from the component.
 * - When the `disabled` property is cleared, the `tabindex` attribute is given the value
 *   assigned to `previousTabIndex`.
 * - In this way we preserve the author set `tabindex` at all times.
 * - This class also sets the value of `aria-disabled` attribute to `true` or `false`
 * if disabled property is set/cleared respectively
 * ___
 * Note:
 * If no `tabindex` is specified by default in the code, the component will be assigned a
 * `tabindex` value of 0. This is to make the component tabbable by default.
 * ___
 * @param {Boolean} disabled - If set, disabled styles will be applied
 * @param {String} tabindex - The tabindex global attribute indicates that its component can be focused
 */
export const ControlStateMixin = (superClass) =>
  class extends superClass {
    static get properties() {
      return {
        disabled: { type: Boolean, reflect: true },
        tabindex: { type: String, reflect: true },
      };
    }

    connectedCallback() {
      super.connectedCallback();
    }

    firstUpdated() {
      super.firstUpdated();
      let target = this.getTabTarget();
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', 0);
    }

    attributeChangedCallback(name, oldval, newval) {
      super.attributeChangedCallback(name, oldval, newval);
      if (name === 'disabled') {
        this.setAttribute('aria-disabled', JSON.stringify(this.disabled));
      }
    }

    updated(changedProperties) {
      super.updated(changedProperties);
      if (changedProperties.has('tabindex')) {
        this.handleTabindexChange();
      }
      if (changedProperties.has('disabled')) {
        this.handleDisabledChange();
      }
      const listOfProperties = ['disabled'];
      dispatchAttributeChangedEvent(this, changedProperties, listOfProperties);
    }

    handleDisabledChange() {
      let target = this.getTabTarget();
      if (this.disabled) {
        target.removeAttribute('tabindex');
        this.blur();
      } else {
        if (this.previousTabIndex) {
          target.setAttribute('tabindex', this.previousTabIndex);
        }
      }
    }

    handleTabindexChange() {
      let target = this.getTabTarget();
      // TODO: make previousTabIndex property a private variable when we move to Typescript.
      if (target.hasAttribute('tabindex')) {
        this.previousTabIndex = target.getAttribute('tabindex');
      }
    }

    getTabTarget() {
      return this;
    }
  };
